import React from "react";
import moment from "moment";
import {
  HiCheckCircle,
  HiXCircle,
  HiBan,
  HiClock,
  HiReply,
  HiExclamationCircle,
  HiCurrencyDollar,
  HiGift,
} from "react-icons/hi";
import { PaymentStatusEnum } from "../../../../services/paymentModule";
import { BadgeTone } from "../../../atoms/StatusBadge";
import {
  CurrentPeriod,
  PlanKind,
  SubscriptionState,
} from "./paymentHistoryUtils";

type Translate = (key: string) => string;
type TranslateWith = (key: string, options?: Record<string, unknown>) => string;

export interface BadgeStyle {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  border: string;
  softBg: string;
  dot: string;
  tone: BadgeTone;
}

export interface StatusDisplay {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  label: string;
  tone: BadgeTone;
}

export const STATE_BADGE: Record<SubscriptionState, BadgeStyle> = {
  active: {
    icon: <HiCheckCircle size={20} />,
    color: "text-green-700",
    bgColor: "bg-green-100",
    dot: "bg-green-500",
    softBg: "bg-gray-50",
    border: "border-green-200",
    tone: "success",
  },
  paymentFailed: {
    icon: <HiExclamationCircle size={20} />,
    color: "text-red-700",
    bgColor: "bg-red-100",
    dot: "bg-red-500",
    softBg: "bg-red-50",
    border: "border-red-200",
    tone: "failure",
  },
  suspended: {
    icon: <HiExclamationCircle size={20} />,
    color: "text-amber-700",
    bgColor: "bg-amber-100",
    dot: "bg-amber-500",
    softBg: "bg-amber-50",
    border: "border-amber-200",
    tone: "warning",
  },
  canceled: {
    icon: <HiBan size={20} />,
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    dot: "bg-gray-300",
    softBg: "bg-gray-50",
    border: "border-gray-200",
    tone: "gray",
  },
  ended: {
    icon: <HiClock size={20} />,
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    dot: "bg-gray-300",
    softBg: "bg-gray-50",
    border: "border-gray-200",
    tone: "gray",
  },
};

export const KIND_BADGE: Record<PlanKind, BadgeStyle> = {
  oneOff: {
    icon: <HiCurrencyDollar size={20} />,
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    dot: "bg-blue-400",
    softBg: "bg-gray-50",
    border: "border-blue-200",
    tone: "info",
  },
  free: {
    icon: <HiGift size={20} />,
    color: "text-green-700",
    bgColor: "bg-green-100",
    dot: "bg-green-400",
    softBg: "bg-gray-50",
    border: "border-green-200",
    tone: "success",
  },
  recurring: {
    icon: <HiClock size={20} />,
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    dot: "bg-gray-300",
    softBg: "bg-gray-50",
    border: "border-gray-200",
    tone: "gray",
  },
};

export const planBadgeStyle = (
  kind: PlanKind,
  state: SubscriptionState | null
): BadgeStyle =>
  kind === "recurring" && state ? STATE_BADGE[state] : KIND_BADGE[kind];

export const getStatusDisplay = (
  status: PaymentStatusEnum,
  t: Translate
): StatusDisplay => {
  switch (status) {
    case "SUCCEEDED":
      return {
        icon: <HiCheckCircle size={20} />,
        color: "text-green-600",
        bgColor: "bg-green-100",
        label: t("paymentHistory.status.succeeded"),
        tone: "success",
      };
    case "ACTIVE":
      return {
        icon: <HiClock size={20} />,
        color: "text-blue-600",
        bgColor: "bg-blue-100",
        label: t("paymentHistory.status.active"),
        tone: "info",
      };
    case "CANCELED":
      return {
        icon: <HiBan size={20} />,
        color: "text-gray-600",
        bgColor: "bg-gray-100",
        label: t("paymentHistory.status.canceled"),
        tone: "gray",
      };
    case "ENDED":
      return {
        icon: <HiCheckCircle size={20} />,
        color: "text-gray-600",
        bgColor: "bg-gray-100",
        label: t("paymentHistory.status.ended"),
        tone: "gray",
      };
    case "UNPAID":
      return {
        icon: <HiXCircle size={20} />,
        color: "text-red-600",
        bgColor: "bg-red-100",
        label: t("paymentHistory.status.unpaid"),
        tone: "failure",
      };
    case "ERROR":
      return {
        icon: <HiXCircle size={20} />,
        color: "text-red-600",
        bgColor: "bg-red-100",
        label: t("paymentHistory.status.error"),
        tone: "failure",
      };
    case "PROCESSING":
      return {
        icon: <HiClock size={20} />,
        color: "text-blue-600",
        bgColor: "bg-blue-100",
        label: t("paymentHistory.status.processing"),
        tone: "info",
      };
    case "REFUNDED":
      return {
        icon: <HiReply size={20} />,
        color: "text-purple-600",
        bgColor: "bg-purple-100",
        label: t("paymentHistory.status.refunded"),
        tone: "purple",
      };
    default:
      return {
        icon: <HiClock size={20} />,
        color: "text-gray-600",
        bgColor: "bg-gray-100",
        label: status,
        tone: "gray",
      };
  }
};

export const getRecurrencySuffix = (
  paymentType: string,
  t: Translate
): string => {
  switch (paymentType) {
    case "DAILY":
      return t("paymentModule.userPaymentHistory.perDay");
    case "WEEKLY":
      return t("paymentModule.userPaymentHistory.perWeek");
    case "MONTHLY":
      return t("paymentModule.userPaymentHistory.perMonth");
    case "ANNUALLY":
      return t("paymentModule.userPaymentHistory.perYear");
    default:
      return "";
  }
};

export const getNextDateLabel = (status: PaymentStatusEnum, t: Translate) => {
  switch (status) {
    case "UNPAID":
    case "ERROR":
      return t("paymentHistory.field.nextAttempt");
    case "ENDED":
    case "CANCELED":
      return t("paymentHistory.field.coveredUntil");
    default:
      return t("paymentHistory.field.nextPayment");
  }
};

export const getPaymentStepDisplay = (step: string, t: Translate) => {
  switch (step) {
    case "INITIAL":
      return t("paymentHistory.step.initial");
    case "FINAL":
      return t("paymentHistory.step.final");
    case "RENEWED":
      return t("paymentHistory.step.renewed");
    default:
      return step;
  }
};

export const getPaymentTypeDisplay = (type: string, t: Translate) => {
  switch (type) {
    case "SUBSCRIPTION":
      return t("paymentHistory.type.subscription");
    case "UNIQUE":
      return t("paymentHistory.type.unique");
    case "FREE":
      return t("paymentHistory.type.free");
    default:
      return type;
  }
};

const ONE_OFF_PLAN = /^One-time payments in ([A-Z]{3})$/;

/** The plan PayMod keeps an entity's one-off payments in, for a currency, carries an English name: shown translated. */
export const planTitle = (description: string, t: TranslateWith): string => {
  const oneOff = description.match(ONE_OFF_PLAN);
  return oneOff
    ? t("paymentHistory.oneOffPlan", { currency: oneOff[1] })
    : description;
};

export const getPlanTypeLabel = (paymentType: string, t: Translate): string => {
  switch (paymentType) {
    case "DAILY":
      return t("paymentModule.recurrencyBadge.daily");
    case "WEEKLY":
      return t("paymentModule.recurrencyBadge.weekly");
    case "MONTHLY":
      return t("paymentModule.recurrencyBadge.monthly");
    case "ANNUALLY":
    case "YEARLY":
      return t("paymentModule.recurrencyBadge.annually");
    case "UNIQUE":
      return t("paymentHistory.type.unique");
    case "FREE":
      return t("paymentHistory.type.free");
    default:
      return paymentType;
  }
};

export const periodSentence = (
  period: CurrentPeriod | null,
  t: TranslateWith
): string | null => {
  if (!period) return null;
  switch (period.status) {
    case "running":
      return period.days === 0
        ? t("paymentHistory.period.runningToday")
        : t("paymentHistory.period.running", {
            date: moment(period.end).format("ll"),
            count: period.days ?? 0,
          });
    case "lapsed":
      return t("paymentHistory.period.lapsed", {
        date: moment(period.end).format("ll"),
        count: period.days ?? 0,
      });
    case "none":
      return t("paymentHistory.period.none");
    default:
      return t("paymentHistory.period.unknown");
  }
};

export const getRefundReasonLabel = (reason: string, t: Translate) => {
  switch (reason) {
    case "REQUESTED_BY_CUSTOMER":
      return t("paymentModule.userPaymentHistory.reasonRequestedByCustomer");
    case "FRAUDULENT":
      return t("paymentModule.userPaymentHistory.reasonFraudulent");
    case "DUPLICATE":
      return t("paymentModule.userPaymentHistory.reasonDuplicate");
    default:
      return reason;
  }
};
